/**
 * SupportRitualBox Component
 *
 * Box component for donating to a donation-based ritual.
 * Appears in the ritual sidebar between info and participants sections.
 * Allows users to donate without joining the ritual.
 */

import { Loader2 } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { GuestAction } from "@/components/guest"
import { useTranslation } from "@/language"
import { createDonation } from "../../services/donation.api"

interface SupportRitualBoxProps {
  ritualId: string
  ritualName?: string
  userDonationAmount: number | null
  onDonateSuccess?: () => void
  isGuest?: boolean
}

export function SupportRitualBox({
  ritualId,
  ritualName: _ritualName,
  userDonationAmount,
  isGuest = false,
}: SupportRitualBoxProps) {
  const [amount, setAmount] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation("rituals")

  const handleAmountChange = (value: string) => {
    const cleanValue = value.replace(/[^0-9.]/g, "")
    const parts = cleanValue.split(".")
    if (parts.length > 2) return
    if (parts[1] && parts[1].length > 2) return
    setAmount(cleanValue)
    setError(null)
  }

  const getNumericAmount = (): number => {
    const num = parseFloat(amount)
    return Number.isNaN(num) ? 0 : num
  }

  const handleDonate = async () => {
    const numericAmount = getNumericAmount()

    if (numericAmount < 1) {
      setError(t("donation.minError"))
      return
    }

    if (numericAmount > 10000) {
      setError(t("donation.maxError"))
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const currentUrl = window.location.href
      const successUrl = `${currentUrl}?donated=true`
      const cancelUrl = currentUrl

      const result = await createDonation(ritualId, numericAmount, successUrl, cancelUrl, false)

      if (result.data.checkoutUrl) {
        window.location.href = result.data.checkoutUrl
      }
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        t("donation.failed")
      toast.error(errorMessage)
      setIsLoading(false)
    }
  }

  return (
    <div className="rounded-[20px] p-5 bg-card">
      {/* Title */}
      <h3 className="text-[16px] font-semibold leading-[19.5px]" style={{ color: "#f2f0eb" }}>
        Support this ritual
      </h3>

      {/* Description */}
      <p className="mt-2 text-[14px] leading-[22px]" style={{ color: "rgba(242, 240, 235, 0.7)" }}>
        Your contribution helps support the ritualist and allows more experiences like this to
        continue.
      </p>

      {/* Amount Input */}
      <div className="mt-4">
        <label
          className="text-[12px] uppercase tracking-wide"
          style={{ color: "rgba(242, 240, 235, 0.5)" }}
        >
          Your donation amount
        </label>
        <div className="mt-1 flex items-center">
          <span className="text-[14px] font-semibold" style={{ color: "#f2f0eb" }}>
            $
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={e => handleAmountChange(e.target.value)}
            placeholder="0"
            className="ml-1 flex-1 bg-transparent text-[14px] font-semibold outline-none"
            style={{ color: "#f2f0eb" }}
          />
        </div>
        <div className="mt-1 h-px w-full" style={{ background: "rgba(242, 240, 235, 0.2)" }} />
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-3 text-[14px]" style={{ color: "#ef4444" }}>
          {error}
        </p>
      )}

      {/* Donate Button */}
      {isGuest ? (
        <GuestAction>
          <button
            className="flex h-[46px] w-full items-center justify-center rounded-[100px] text-[14px] font-semibold uppercase tracking-wide transition-opacity cursor-pointer border border-white my-8"
            style={{
              color: "#ffffff",
            }}
          >
            {t("donation.donate")}
          </button>
        </GuestAction>
      ) : (
        <button
          onClick={handleDonate}
          disabled={isLoading || getNumericAmount() < 1}
          className="flex h-[46px] w-full items-center justify-center rounded-[100px] text-[14px] font-semibold uppercase tracking-wide transition-opacity cursor-pointer disabled:cursor-not-allowed border border-white my-8"
          style={{
            color: "#ffffff",
            opacity: isLoading || getNumericAmount() < 1 ? 0.5 : 1,
          }}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            t("donation.donate")
          )}
        </button>
      )}

      {/* Already Donated Message */}
      {userDonationAmount !== null && userDonationAmount > 0 && (
        <p className="mt-3 text-[14px] text-brand-olive">
          You've donated ${userDonationAmount} to this ritual organizer
        </p>
      )}
    </div>
  )
}
